<template>
  <div class="chat-group">
    <div v-if="hasUserMessage" class="chat-block chat-block--user">
      <v-hover v-slot="{ hover }">
        <div v-if="hasUserText" class="chat-row chat-row--user">
          <div class="chat-bubble chat-bubble--user">
            <div class="chat-bubble__inner">
              <div v-if="hover && userCopyText" class="chat-bubble__actions">
                <!-- <v-btn
                  class="chat-bubble__actions-btn"
                  icon
                  aria-label="Share"
                  title="Share prompt"
                  @click="handleSharePrompt()"
                >
                  <v-icon size="16">mdi-share-outline</v-icon>
                </v-btn> -->
                <v-btn
                  class="chat-bubble__actions-btn"
                  icon
                  aria-label="Edit"
                  title="Copy text"
                  @click="handleCopyPlain(userCopyText)"
                >
                  <v-icon size="16">{{ copied ? 'mdi-check' : 'mdi-content-copy' }}</v-icon>
                </v-btn>
              </div>
              <div>
                <div
                  :id="userMessageTextId"
                  ref="userMessageTextRef"
                  class="chat-bubble__text chat-bubble__text--user"
                  :class="{
                    'chat-bubble__text--user-expanded': userMessageExpanded,
                  }"
                >
                  {{ message.userMessage }}
                </div>
                <div class="chat-bubble__user-text-toggle-container">
                  <button
                    v-if="showUserMessageExpandToggle"
                    type="button"
                    class="chat-bubble__user-text-toggle"
                    :aria-expanded="userMessageExpanded ? 'true' : 'false'"
                    :aria-controls="userMessageTextId"
                    @click="toggleUserMessageExpanded"
                  >
                    {{ userMessageExpanded ? 'Show less' : 'Show more' }}
                    <v-icon size="18">{{ userMessageExpanded ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
                  </button>
                  <div
                    v-if="!userMessageExpanded && showUserMessageExpandToggle"
                    class="chat-bubble__user-text-toggle-overlay"
                  ></div>
                </div>
                <div v-if="timeLabel" class="chat-bubble__time chat-bubble__time--user">{{ timeLabel }}</div>
              </div>
            </div>
          </div>
        </div>
      </v-hover>

      <div v-if="hasRedesignCompare" class="chat-row chat-row--user chat-row--user-attachments">
        <div
          class="message-attachments-below message-attachments-below--user redesign-msg-compare"
          role="group"
          aria-label="Product and reference images"
        >
          <div class="redesign-msg-compare__card">
            <span class="redesign-msg-compare__label">Product</span>
            <div class="redesign-msg-compare__thumbs" role="list" aria-label="Product images">
              <button
                v-for="att in userAttachmentItems"
                :key="att.id"
                type="button"
                class="redesign-msg-compare__thumb"
                role="listitem"
                aria-label="Preview image"
                title="Preview image"
                @click="openPreview(att)"
              >
                <img class="redesign-msg-compare__img" :src="att.thumbUrl" :alt="att.name" loading="lazy" />
              </button>
            </div>
          </div>
          <span class="redesign-msg-compare__vs" aria-hidden="true">vs</span>
          <div class="redesign-msg-compare__card">
            <span class="redesign-msg-compare__label">Reference</span>
            <div class="redesign-msg-compare__thumbs" role="list" aria-label="Reference images">
              <button
                v-for="att in userImageRefItems"
                :key="att.id"
                type="button"
                class="redesign-msg-compare__thumb"
                role="listitem"
                aria-label="Preview image"
                title="Preview image"
                @click="openPreview(att)"
              >
                <img class="redesign-msg-compare__img" :src="att.thumbUrl" :alt="att.name" loading="lazy" />
              </button>
            </div>
          </div>
        </div>
      </div>

      <div v-else-if="hasUserStripAttachments" class="chat-row chat-row--user chat-row--user-attachments">
        <div class="message-attachments-below message-attachments-below--user">
          <div class="attachments-scroll attachments-scroll--user" role="list" aria-label="Uploaded images">
            <div class="attachments-scroll__inner attachments-scroll__inner--user">
              <div v-for="att in visibleUserAttachments" :key="att.id" class="attachment" role="listitem">
                <button
                  type="button"
                  class="attachment__thumb"
                  :aria-label="isVideo(att) ? 'Preview video' : 'Preview image'"
                  :title="isVideo(att) ? 'Preview video' : 'Preview image'"
                  @click="openPreview(att)"
                >
                  <div v-if="isVideo(att)" class="attachment__thumb--video">
                    <v-icon class="attachment__thumb--video-icon" size="22" color="white">mdi-play</v-icon>
                    <video class="attachment__img" :src="att.thumbUrl" :alt="att.name" />
                  </div>
                  <img v-else class="attachment__img" :src="att.thumbUrl" :alt="att.name" loading="lazy" />
                </button>
              </div>
              <button
                v-if="hiddenUserAttachmentCount > 0"
                type="button"
                class="attachments-more"
                :aria-label="`Show ${hiddenUserAttachmentCount} more`"
                :title="`Show ${hiddenUserAttachmentCount} more`"
                @click="stripExpandedUser = true"
              >
                +{{ hiddenUserAttachmentCount }}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div v-if="showUserTimeBelowAttachments" class="chat-row chat-row--user">
        <span class="chat-time-below chat-time-below--user">{{ timeLabel }}</span>
      </div>
    </div>

    <div v-if="showFailedActions" class="chat-row chat-row--user">
      <div class="chat-actions">
        <v-btn v-if="message._localOnly" text color="error" class="chat-actions__btn" @click="$emit('delete', message)">
          Remove
        </v-btn>
        <v-btn text color="primary" class="chat-actions__btn" @click="$emit('retry', message)">Retry</v-btn>
      </div>
    </div>

    <div v-if="hasAiMessage" class="chat-block chat-block--assistant">
      <div v-if="hasAiText" class="chat-row">
        <div class="chat-bubble">
          <div class="chat-bubble__inner">
            <div class="chat-bubble__text chat-bubble__text--ai" v-html="aiReplyHtml" @click="handleCodeBlockAction" />
            <!-- <div v-if="timeLabel" class="chat-bubble__time chat-bubble__time--ai">{{ timeLabel }}</div> -->
          </div>
        </div>
      </div>

      <div v-if="hasAiAttachments" class="chat-row">
        <div class="message-attachments-below message-attachments-below--assistant">
          <div class="attachments-stack attachments-stack--assistant" role="list" aria-label="AI images">
            <div v-for="att in visibleAiAttachments" :key="att.id" class="ai-attachment-row" role="listitem">
              <v-hover v-slot="{ hover }">
                <div
                  class="ai-attachment__btn"
                  :aria-label="isVideo(att) ? 'Preview video' : 'Preview image'"
                  :title="isVideo(att) ? 'Preview video' : 'Preview image'"
                  @click="openPreview(att)"
                >
                  <img class="ai-attachment__img" :src="att.fullUrl" :alt="att.name" loading="lazy" />
                  <div v-if="hover" class="ai-attachment__actions">
                    <v-btn title="Copy image" icon @click.stop="handleCopyPlain(att.fullUrl)">
                      <v-icon size="16" color="black">{{ copied ? 'mdi-check' : 'mdi-content-copy' }}</v-icon>
                    </v-btn>
                    <a :href="att.fullUrl" target="_blank" download @click.stop>
                      <v-btn icon title="Download image">
                        <v-icon size="18" color="black">mdi-download</v-icon>
                      </v-btn>
                    </a>
                  </div>
                </div>
              </v-hover>
            </div>
            <button
              v-if="hiddenAiAttachmentCount > 0"
              type="button"
              class="attachments-more attachments-more--stack"
              :aria-label="`Show ${hiddenAiAttachmentCount} more`"
              :title="`Show ${hiddenAiAttachmentCount} more`"
              @click="stripExpandedAi = true"
            >
              +{{ hiddenAiAttachmentCount }}
            </button>
          </div>
        </div>
      </div>

      <PromptConcepts :concepts="message.concepts" v-if="hasAiText" @select-concept="onSelectConcept" />

      <div class="chat-row" v-if="aiErrorMessage">
        <v-icon size="16" color="red">mdi-alert-circle</v-icon>
        <span class="chat-error-message">{{ aiErrorMessage }}</span>
      </div>

      <div v-if="showAiTimeBelowAttachments" class="chat-row">
        <!-- <span class="chat-time-below">{{ timeLabel }}</span> -->
      </div>
    </div>

    <div v-else-if="showAssistantLoading" class="chat-block chat-block--assistant">
      <div v-if="showImageGenLoading" class="chat-row">
        <div class="message-attachments-below" style="width: 100%;">
          <div class="attachments-stack attachments-stack--assistant">
            <div
              style="width: 100%; max-width: 420px;"
              role="status"
              aria-live="polite"
              aria-busy="true"
              aria-label="Generating image"
            >
              <span class="image-gen-loading__label">Creating image…</span>
              <div class="ai-attachment__btn image-gen-loading__btn">
                <div class="ai-attachment__img image-gen-loading__placeholder" aria-hidden="true">
                  <div class="image-gen-loading__surface">
                    <div class="image-gen-loading__aurora" />
                    <div class="image-gen-loading__orb image-gen-loading__orb--1" />
                    <div class="image-gen-loading__orb image-gen-loading__orb--2" />
                    <div class="image-gen-loading__shimmer" />
                    <v-icon class="image-gen-loading__icon" size="28" aria-hidden="true">mdi-auto-fix</v-icon>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div v-else-if="showTextTypingLoading" class="chat-row">
        <div class="chat-bubble chat-bubble--processing">
          <div class="chat-bubble__inner" style="padding: 8px 14px">
            <div class="typing-bubble" aria-live="polite" aria-busy="true">
              <span class="typing-bubble__dot" />
              <span class="typing-bubble__dot" />
              <span class="typing-bubble__dot" />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { copyPlain } from './utils';
import moment from 'moment';
import imageHelpers from '@/helpers/image';
import { renderMarkdown } from './utils/markdown';
import { chatbotApi } from '@/apis/chatbot';
import PromptConcepts from './PromptConcepts.vue';
import { isVideoUrl } from './utils';

const GENERATE_AD_IMAGE_INTENT = 'GENERATE_AD_IMAGE';

export default {
  name: 'ChatbotMessageItem',
  components: { PromptConcepts },
  props: {
    message: {
      type: Object,
      required: true,
    },
    product: {
      type: Object,
      required: true,
    },
    isLastMessage: {
      type: Boolean,
      default: false,
    },
    forceAdImageGen: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      copied: false,
      stripExpandedUser: false,
      stripExpandedAi: false,
      userMessageExpanded: false,
      userMessageCanExpand: false,
      userMessageResizeObserver: null,
      aiReplyHtml: '',
      streamingDisplayText: '',
      streamingTargetText: '',
      streamingRafId: null,
    };
  },
  computed: {
    hasUserText() {
      return this.message && String(this.message.userMessage || '').trim().length > 0;
    },
    userCopyText() {
      return this.message && this.message.userMessage != null ? String(this.message.userMessage).trim() : '';
    },
    userAttachmentItems() {
      return this.parseAttachmentList(this.message && this.message.attachments);
    },
    userImageRefItems() {
      return this.parseAttachmentList(this.message && this.message.image_ref);
    },
    hasUserAttachments() {
      return this.userAttachmentItems.length > 0;
    },
    hasUserImageRef() {
      return this.userImageRefItems.length > 0;
    },
    userStripAttachmentItems() {
      if (
        this.message &&
        this.message.mode === 'edit' &&
        !this.userAttachmentItems.length &&
        this.userImageRefItems.length
      ) {
        return this.userImageRefItems;
      }
      return this.userAttachmentItems;
    },
    hasUserStripAttachments() {
      return this.userStripAttachmentItems.length > 0;
    },
    hasRedesignCompare() {
      return this.message && this.message.mode === 'redesign' && (this.hasUserAttachments || this.hasUserImageRef);
    },
    hasUserMessage() {
      return this.hasUserText || this.hasUserAttachments || this.hasUserImageRef;
    },
    visibleUserAttachments() {
      if (this.stripExpandedUser) return this.userStripAttachmentItems;
      return this.userStripAttachmentItems.slice(0, this.maxPreviewThumbs);
    },
    hiddenUserAttachmentCount() {
      if (this.stripExpandedUser) return 0;
      return Math.max(0, this.userStripAttachmentItems.length - this.maxPreviewThumbs);
    },
    hasAiText() {
      return this.message && String(this.message.aiReply || '').trim().length > 0;
    },
    aiAttachmentItems() {
      return this.parseAttachmentList(this.message && this.message.attachments_output);
    },
    hasAiAttachments() {
      return this.aiAttachmentItems.length > 0;
    },
    hasAiMessage() {
      return this.hasAiText || this.hasAiAttachments || this.aiErrorMessage;
    },
    visibleAiAttachments() {
      if (this.stripExpandedAi) return this.aiAttachmentItems;
      return this.aiAttachmentItems.slice(0, this.maxPreviewThumbs);
    },
    hiddenAiAttachmentCount() {
      if (this.stripExpandedAi) return 0;
      return Math.max(0, this.aiAttachmentItems.length - this.maxPreviewThumbs);
    },
    aiReplyRaw() {
      return this.message && this.message.aiReply != null ? this.message.aiReply : '';
    },
    aiErrorMessage() {
      return this.message && this.message.errorMessage != null ? this.message.errorMessage : '';
    },
    maxPreviewThumbs() {
      return 3;
    },
    messageBaseId() {
      const m = this.message;
      return (m && (m._key || m._id)) || 'msg';
    },
    localStatus() {
      return this.message && this.message._localOnly ? this.message._localStatus : null;
    },
    isMessageLocalFailed() {
      return this.localStatus === 'failed';
    },
    isMessageServerFailed() {
      return !!(this.message.errorMessage && (this.message.status === 'failed' || this.message.status === 'stopped'));
    },
    isMessageFailed() {
      return this.isMessageLocalFailed || this.isMessageServerFailed;
    },
    isMessageProcessing() {
      return this.isMessageLocalProcessing || this.isMessageServerProcessing;
    },
    isMessageLocalProcessing() {
      return this.localStatus === 'processing' && !this.isMessageFailed;
    },
    isMessageServerProcessing() {
      return !!(this.message && !this.isMessageFailed && this.message.status === 'processing' && !this.hasAiMessage);
    },
    showTextTypingLoading() {
      return this.isMessageLocalProcessing && !this.isImageGenMode && !this.hasAiText;
    },
    showAssistantLoading() {
      if (this.isMessageFailed) return false;
      if (this.showImageGenLoading || this.showTextTypingLoading) return true;
      return this.isMessageServerProcessing;
    },
    showFailedActions() {
      return this.isMessageLocalFailed || this.isMessageServerFailed;
    },
    timeLabel() {
      const raw = this.message && this.message.createdAt ? this.message.createdAt : null;
      if (!raw) return '';
      return moment(raw).format('h:mm A');
    },
    showUserTimeBelowAttachments() {
      return !this.hasUserText && (this.hasUserAttachments || this.hasUserImageRef) && !!this.timeLabel;
    },
    showAiTimeBelowAttachments() {
      return !this.hasAiText && this.hasAiAttachments && !!this.timeLabel;
    },
    userMessageTextId() {
      return `chat-user-msg-${this.messageBaseId}`;
    },
    showUserMessageExpandToggle() {
      return this.userMessageCanExpand || this.userMessageExpanded;
    },
    isImageGenMode() {
      if (this.isMessageFailed) return false;
      if (this.forceAdImageGen) return true;
      const intent = this.message && this.message.intent != null ? String(this.message.intent).trim() : '';
      if (intent === GENERATE_AD_IMAGE_INTENT) return true;
      const mode = this.message && this.message.mode;
      return mode === 'edit' || mode === 'redesign' || mode === 'concept';
    },
    showImageGenLoading() {
      if (this.isMessageFailed || this.hasAiMessage || !this.isImageGenMode) {
        return false;
      }
      return this.isMessageServerProcessing || this.isMessageLocalProcessing;
    },
  },
  watch: {
    // ─── OLD: immediate render (no animation) ───
    // aiReplyRaw: {
    //   immediate: true,
    //   handler(reply) {
    //     this.aiReplyHtml = renderMarkdown(reply);
    //   },
    // },
    aiReplyRaw: {
      immediate: true,
      handler(newReply) {
        this.streamingTargetText = newReply;

        if (!this.message._localOnly || !newReply) {
          this.stopStreamingAnimation();
          this.streamingDisplayText = newReply;
          this.aiReplyHtml = renderMarkdown(newReply);
          return;
        }

        this.startStreamingAnimation();
      },
    },
    'message.userMessage'() {
      this.userMessageExpanded = false;
      this.$nextTick(() => this.updateOverflowState());
    },
  },
  mounted() {
    this.initOverflowObserver();
  },
  beforeDestroy() {
    this.destroyOverflowObserver();
    this.stopStreamingAnimation();
  },
  methods: {
    startStreamingAnimation() {
      if (this.streamingRafId) return;
      this.tickStreamingAnimation();
    },
    stopStreamingAnimation() {
      if (this.streamingRafId) {
        cancelAnimationFrame(this.streamingRafId);
        this.streamingRafId = null;
      }
    },
    tickStreamingAnimation() {
      const target = this.streamingTargetText;
      const current = this.streamingDisplayText;

      if (current.length >= target.length) {
        this.streamingRafId = null;
        return;
      }

      const gap = target.length - current.length;
      const charsPerFrame = gap > 100 ? 8 : gap > 50 ? 5 : 3;
      const nextLen = Math.min(current.length + charsPerFrame, target.length);
      this.streamingDisplayText = target.slice(0, nextLen);
      this.aiReplyHtml = renderMarkdown(this.streamingDisplayText);

      this.streamingRafId = requestAnimationFrame(() => this.tickStreamingAnimation());
    },
    handleCodeBlockAction(e) {
      const actionEl = e.target.closest('[data-action]');
      if (!actionEl) return;
      const action = actionEl.dataset.action;
      const wrapper = actionEl.closest('.code-block-wrapper');

      if (!wrapper) return;
      const prevWrapper = wrapper?.previousElementSibling;
      const conceptTitle = prevWrapper && prevWrapper?.tagName === 'P' ? prevWrapper?.textContent?.trim() : '';

      const pre = wrapper.querySelector('pre');
      const blockquote = wrapper.querySelector('blockquote');
      if (!pre && !blockquote) return;
      const codeText = (pre?.textContent || blockquote?.textContent || '').trim();
      if (!codeText) return;

      if (action === 'copy-code') {
        this.handleCopyPlain(codeText);
      } else if (action === 'send-code') {
        this.$emit('open-image-generation', { title: conceptTitle, description: codeText });
      }
    },
    onSelectConcept(concept) {
      this.$emit('open-image-generation', concept);
    },
    parseAttachmentList(raw) {
      if (!Array.isArray(raw) || raw.length === 0) return [];
      const base = this.messageBaseId;
      const out = [];
      raw.forEach((entry, i) => {
        const item = this.parseAttachmentItem(entry, i);
        if (!item) return;
        out.push({
          id: `${base}::att::${i}::${item.rawKey}`,
          name: item.name,
          thumbUrl: item.thumbUrl,
          fullUrl: item.fullUrl,
        });
      });
      return out;
    },
    parseAttachmentItem(entry, idx) {
      let raw = '';
      let name = `Image ${idx + 1}`;
      if (typeof entry === 'string') {
        raw = entry.trim();
      } else if (entry && typeof entry === 'object') {
        raw = String(entry.src || entry.url || entry.imagePath || entry.path || '').trim();
        if (entry.name != null && String(entry.name).trim()) name = String(entry.name).trim();
      }
      if (!raw) return null;
      const absolute = /^https?:\/\//i.test(raw) || raw.startsWith('data:') || raw.startsWith('blob:');
      const thumbUrl = absolute ? raw : imageHelpers.url(raw, { width: 128, height: 128 });
      const fullUrl = absolute ? raw : imageHelpers.url(raw, { width: 1024, height: 1024 });
      const rawKey = raw.length > 24 ? raw.slice(0, 24) : raw;
      return { rawKey, name, thumbUrl, fullUrl };
    },
    isVideo(att) {
      return isVideoUrl(att.fullUrl);
    },
    flashCopied() {
      this.copied = true;
      setTimeout(() => {
        this.copied = false;
      }, 1000);
    },
    async handleCopyPlain(text) {
      try {
        await copyPlain(text);
        this.flashCopied();
      } catch (error) {
        console.error('Error while copying text:', error);
        this.$store.commit('setMessages', {
          type: 'error',
          messages: 'Missing type!',
        });
      }
    },
    initOverflowObserver() {
      this.$nextTick(() => {
        const el = this.$refs.userMessageTextRef;
        if (!el) {
          this.updateOverflowState();
          return;
        }
        this.destroyOverflowObserver();
        if (typeof ResizeObserver !== 'undefined') {
          this.userMessageResizeObserver = new ResizeObserver(() => {
            this.updateOverflowState();
          });
          this.userMessageResizeObserver.observe(el);
        }
        this.updateOverflowState();
      });
    },
    destroyOverflowObserver() {
      if (this.userMessageResizeObserver) {
        this.userMessageResizeObserver.disconnect();
        this.userMessageResizeObserver = null;
      }
    },
    updateOverflowState() {
      const el = this.$refs.userMessageTextRef;
      if (!el || !this.hasUserText) {
        this.userMessageCanExpand = false;
        return;
      }
      if (this.userMessageExpanded) {
        this.userMessageCanExpand = true;
        return;
      }
      this.userMessageCanExpand = el.scrollHeight > el.clientHeight + 1;
    },
    toggleUserMessageExpanded() {
      this.userMessageExpanded = !this.userMessageExpanded;
      this.$nextTick(() => this.updateOverflowState());
    },
    openPreview(att) {
      if (!att || !att.fullUrl) return;
      this.$emit('open-preview', att);
    },
    async handleSharePrompt() {
      if (!this.product._id) return;
      if (confirm('Are you sure you want to share this prompt with the catalog?')) {
        try {
          const { data } = await chatbotApi.sharePromptChat({
            productId: this.product._id,
            prompt_id: this.message._id,
          });
          if (data?.status) {
            this.$store.commit('setMessages', {
              type: 'success',
              messages: 'Prompt shared successfully!',
            });
          } else {
            this.$store.commit('setMessages', {
              type: 'error',
              messages: 'Error while sharing prompt!',
            });
          }
        } catch (error) {
          console.error('Error while sharing prompt:', error);
          this.$store.commit('setMessages', {
            type: 'error',
            messages: 'Error while sharing prompt!',
          });
        }
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.chat-group {
  display: flex;
  flex-direction: column;
  gap: 24px;
  margin-bottom: 16px;
  width: 100%;
  max-width: 100%;
  min-width: 0;
}

.chat-block {
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
  max-width: 100%;
  min-width: 0;
}

.chat-block--user {
  align-items: flex-end;
}

.chat-block--assistant {
  align-items: stretch;
  margin-top: 12px;

  .chat-bubble {
    box-shadow: none;
    border: none;
    .chat-bubble__inner {
      padding: 0;
    }
  }
}

.chat-row {
  display: flex;
  contain: layout style;
  width: 100%;
  max-width: 100%;
  min-width: 0;
}

.chat-row--user {
  justify-content: flex-end;
}

.chat-row--user-attachments {
  display: flex;
  justify-content: flex-end;
}

.message-attachments-below {
  flex: 0 1 auto;
  max-width: 100%;
  min-width: 0;
  overflow: hidden;
}

.message-attachments-below--assistant {
  max-width: 100%;
}

.message-attachments-below--user {
  flex: 0 1 70%;
  max-width: 70%;
  width: 100%;
}

.message-attachments-below--user.redesign-msg-compare {
  flex: 0 1 auto;
  max-width: none;
  width: auto;
}

.redesign-msg-compare {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border-radius: 12px;
  background: var(--main-background, #e9eef6);
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.04);
}

.redesign-msg-compare__card {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  min-width: 0;
}

.redesign-msg-compare__label {
  margin: 0;
  font-size: 12px;
  font-weight: 500;
  letter-spacing: 0.06em;
  line-height: 1;
  color: rgba(0, 0, 0, 0.5);
  white-space: nowrap;
}

.redesign-msg-compare__thumbs {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: 6px;
}

.redesign-msg-compare__thumb {
  display: block;
  width: 64px;
  height: 64px;
  padding: 0;
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 12px;
  overflow: hidden;
  background: #fff;
  cursor: pointer;
  line-height: 0;
  transition: box-shadow 0.15s ease, transform 0.15s ease;
}

.redesign-msg-compare__img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.redesign-msg-compare__vs {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  border-radius: 999px;
  font-size: 10px;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  background: #fff;
  color: rgba(0, 0, 0, 0.5);
}

.chat-time-below {
  font-size: 12px;
  line-height: 1.2;
  letter-spacing: 0.02em;
}

.chat-time-below--user {
  text-align: right;
}

.chat-bubble {
  position: relative;
  border-radius: 12px;
  max-width: 100%;
}

.chat-bubble--processing {
  background: var(--main-background);
}

.chat-row--user .chat-bubble {
  max-width: 70%;
  min-width: 80px;
}

.chat-bubble--user {
  background: var(--main-background);
  border-color: transparent;
}

.chat-bubble__inner {
  position: relative;
  padding: 12px 12px 8px 12px;
}

.attachments-scroll {
  width: 100%;
  max-width: 100%;
  min-width: 0;
  padding-bottom: 2px;
  padding-top: 4px;
}

.attachments-scroll--user {
  display: block;
}

.attachments-scroll__inner--user {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px;
  justify-content: flex-end;
  width: 100%;
  min-width: min-content;
}

/* AI: one image per row (stack), like common AI chat UIs */
.attachments-stack--assistant {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 10px;
  width: 100%;
  max-width: 100%;
  min-width: 0;
}

.ai-attachment-row {
  width: 100%;
  max-width: min(560px, 100%);
}

.ai-attachment__btn {
  position: relative;
  display: block;
  width: 100%;
  padding: 0;
  border-radius: 16px;
  overflow: hidden;
  background: var(--main-background);
  cursor: pointer;
  line-height: 0;
  border: 1px solid rgba(0, 0, 0, 0.08);
}

.ai-attachment__img {
  display: block;
  width: 100%;
  height: auto;
  max-height: 420px;
  object-fit: contain;
}

.ai-attachment__actions {
  position: absolute;
  top: 4px;
  right: 4px;
  z-index: 1;
  display: flex;
  gap: 4px;

  button {
    border: 1px solid rgba(0, 0, 0, 0.08);
    background: var(--main-background);
    height: 32px;
    width: 32px;
  }
}

.attachments-more--stack {
  align-self: flex-start;
}

.attachment {
  position: relative;
  flex: 0 0 auto;
}

.attachment__thumb {
  position: relative;
  width: 64px;
  height: 64px;
  border-radius: 12px;
  border: 1px solid rgba(0, 0, 0, 0.08);
  background: #f6f6f6;
  overflow: hidden;
  cursor: pointer;
  padding: 0;
  display: grid;
  place-items: center;

  &--video {
    position: relative;
    display: grid;
    place-items: center;
    &::before {
      content: '';
      position: absolute;
      inset: 0;
      border-radius: 8px;
      background: rgba(0, 0, 0, 0.5);
      z-index: 1;
    }
    &-icon {
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      z-index: 2;
    }
  }
}

.attachment__img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.attachments-more {
  flex: 0 0 auto;
  width: 64px;
  height: 64px;
  padding: 0 10px;
  border-radius: 8px;
  border: 1px dashed rgba(0, 0, 0, 0.18);
  background: rgba(0, 0, 0, 0.03);
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  transition: background 0.3s ease;

  &:hover {
    background: var(--main-background);
  }
}

.chat-bubble__time {
  text-align: right;
  width: 100%;
  font-size: 12px;
  line-height: 1.1;
  letter-spacing: 0.02em;
  pointer-events: none;
}

.chat-bubble__copy {
  position: absolute;
  top: 6px;
  right: 6px;
  z-index: 1;
  background: var(--main-background) !important;
  color: var(--main-text-color) !important;
  height: 32px !important;
  width: 32px !important;

  &--ai {
    top: 0;
    right: 0;
  }
}

.chat-bubble__actions {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  left: -44px;
  z-index: 1;
  color: var(--main-text-color) !important;
  height: 32px !important;
  width: 32px !important;
  display: flex;
  gap: 4px;

  .chat-bubble__actions-btn {
    background: var(--main-background) !important;
    color: var(--main-text-color) !important;
    height: 32px !important;
    width: 32px !important;
  }
}

.chat-bubble__text {
  white-space: pre-line;
  font-size: 14px;
  line-height: 1.55;
  word-break: break-word;
  color: var(--main-text-color);

  &:not(:empty) {
    margin-bottom: 4px;
  }

  &--user {
    -webkit-line-clamp: 8;
    line-clamp: 8;
    display: -webkit-box;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }

  &--user-expanded {
    display: block;
    -webkit-line-clamp: unset;
    line-clamp: unset;
    -webkit-box-orient: unset;
    overflow: visible;
  }

  &--ai {
    white-space: normal;
  }
}

.chat-actions {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  padding-right: 2px;
  margin-top: -8px;
}

.chat-actions__btn {
  text-transform: none;
  height: 24px !important;
  min-width: 24px !important;
  padding: 0 8px !important;
  font-size: 12px !important;
  font-weight: 500 !important;
}

.chat-actions__btn--danger {
  color: #e53935 !important;
}

.chat-error-message {
  color: #e53935;
  margin-left: 4px;
}

.typing-bubble {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: transparent;
  border-radius: 10px;
}

.typing-bubble__dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.45);
  animation: chatbot-dot 1.2s infinite ease-in-out both;
}

.typing-bubble__dot:nth-child(2) {
  animation-delay: 0.15s;
}
.typing-bubble__dot:nth-child(3) {
  animation-delay: 0.3s;
}

@keyframes chatbot-dot {
  0%,
  80%,
  100% {
    transform: scale(0.6);
    opacity: 0.45;
  }
  40% {
    transform: scale(1);
    opacity: 1;
  }
}

.image-gen-loading__btn {
  cursor: default;
  pointer-events: none;
  line-height: normal;
  width: 100%;
}

/* Extends .ai-attachment__img — full row width, square cap like typical ad output */
.ai-attachment__img.image-gen-loading__placeholder {
  position: relative;
  aspect-ratio: 1;
  height: auto;
  max-height: 420px;
  overflow: hidden;
  background: #e8ecf2;
}

.image-gen-loading__surface {
  position: absolute;
  inset: 0;
  overflow: hidden;
}

.image-gen-loading__aurora {
  position: absolute;
  inset: -40%;
  background: conic-gradient(
    from 0deg at 50% 50%,
    #c4d4ff 0deg,
    #e8d4ff 72deg,
    #ffd6e8 144deg,
    #d4f0ff 216deg,
    #c4d4ff 360deg
  );
  opacity: 0.55;
  animation: image-gen-aurora-spin 8s linear infinite;
}

.image-gen-loading__orb {
  position: absolute;
  border-radius: 50%;
  filter: blur(28px);
  opacity: 0.7;
  animation: image-gen-orb-float 3.2s ease-in-out infinite;
}

.image-gen-loading__orb--1 {
  width: 55%;
  height: 55%;
  top: 10%;
  left: -8%;
}

.image-gen-loading__orb--2 {
  width: 50%;
  height: 50%;
  bottom: 5%;
  right: -10%;
  animation-delay: -1.4s;
}

.image-gen-loading__shimmer {
  position: absolute;
  inset: 0;
  background: linear-gradient(
    105deg,
    transparent 30%,
    rgba(255, 255, 255, 0.15) 42%,
    rgba(255, 255, 255, 0.85) 50%,
    rgba(255, 255, 255, 0.15) 58%,
    transparent 70%
  );
  animation: image-gen-sweep 1.4s ease-in-out infinite;
}

.image-gen-loading__icon {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  color: rgba(255, 255, 255, 0.95);
  filter: drop-shadow(0 1px 6px rgba(80, 100, 180, 0.45));
  animation: image-gen-icon-pulse 1.8s ease-in-out infinite;
  z-index: 1;
}

.image-gen-loading__label {
  display: block;
  margin-bottom: 8px;
  font-size: 13px;
  color: rgba(0, 0, 0, 0.45);
}

@keyframes image-gen-aurora-spin {
  to {
    transform: rotate(360deg);
  }
}

@keyframes image-gen-orb-float {
  0%,
  100% {
    transform: translate(0, 0) scale(1);
  }
  50% {
    transform: translate(6%, -5%) scale(1.08);
  }
}

@keyframes image-gen-sweep {
  0% {
    transform: translateX(-100%);
  }
  100% {
    transform: translateX(100%);
  }
}

@keyframes image-gen-icon-pulse {
  0%,
  100% {
    opacity: 0.75;
    transform: translate(-50%, -50%) scale(0.92);
  }
  50% {
    opacity: 1;
    transform: translate(-50%, -50%) scale(1.06);
  }
}

.chat-bubble__user-text-toggle-container {
  position: relative;
}

.chat-bubble__user-text-toggle {
  display: block;
  width: 100%;
  padding: 0;
  border: 0;
  background: none;
  font: inherit;
  font-size: 13px;
  font-weight: 500;
  text-align: left;
  cursor: pointer;
  margin-top: 8px;
  position: relative;
  z-index: 2;
  &:hover {
    text-decoration: underline;
  }
}

.chat-bubble__user-text-toggle-overlay {
  position: absolute;
  top: -80px;
  left: 0;
  width: 100%;
  height: 80px;
  background: linear-gradient(to bottom, transparent, var(--main-background));
  z-index: 1;
  pointer-events: none;
}

.chat-bubble--user .chat-bubble__text strong {
  color: inherit;
  font-weight: 700;
}
button.btn.v-btn--outlined,
button.v-btn.v-btn--outlined {
  height: 28px !important;
}

/* Custom scrollbar */
::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

::-webkit-scrollbar-track {
  background: transparent;
  border-radius: 999px;
}

::-webkit-scrollbar-thumb {
  background: rgba(0, 0, 0, 0.16);
  border-radius: 999px;
}

::-webkit-scrollbar-thumb:hover {
  background: rgba(0, 0, 0, 0.2);
}
</style>
